/**
 * PATH: apps/quote/src/hooks/use-spreadsheet-grid/createKeyHandler.ts
 * PURPOSE: Factory for the main keyboard event handler
 *
 * WHY: Extracted from useSpreadsheetGrid to keep each file under 300 lines.
 * The keyboard handler is the largest single piece of logic (~180 lines).
 */

import type { CellCoord, GridSelection, ColumnDef } from './types'

interface KeyHandlerDeps {
  activeCell: CellCoord | null
  selection: GridSelection | null
  isEditing: boolean
  editValue: string
  columns: ColumnDef[]
  rowCount: number
  colCount: number
  setSelection: (sel: GridSelection | null) => void
  setIsEditing: (editing: boolean) => void
  setEditValue: (value: string) => void
  navigateTo: (row: number, col: number) => void
  startEditing: () => void
  commitEdit: () => void
  cancelEdit: () => void
  handlePaste: () => void
  handleCopy: () => void
  handleFillDown: () => void
  onCellChange?: (row: number, col: number, value: string) => void
  onFillDown?: (selection: GridSelection, value: string) => void
  findNearestVisibleCol: (startCol: number, direction: 1 | -1) => number
}

export function createKeyHandler(deps: KeyHandlerDeps) {
  return (e: React.KeyboardEvent) => {
    const { activeCell, selection, isEditing, editValue, columns, rowCount, colCount } = deps
    if (!activeCell && !e.ctrlKey && !e.metaKey) return

    const { key, shiftKey, ctrlKey, metaKey } = e
    const isCmd = ctrlKey || metaKey

    // Handle copy (Ctrl+C)
    if (isCmd && key.toLowerCase() === 'c') { deps.handleCopy(); e.preventDefault(); return }
    // Handle paste (Ctrl+V)
    if (isCmd && key.toLowerCase() === 'v') { deps.handlePaste(); e.preventDefault(); return }
    // Handle fill down (Ctrl+D)
    if (isCmd && key.toLowerCase() === 'd' && selection) {
      deps.onFillDown ? deps.onFillDown(selection, editValue) : deps.handleFillDown()
      e.preventDefault(); return
    }

    if (!isEditing) {
      switch (key) {
        case 'ArrowUp':
          e.preventDefault()
          if (activeCell) {
            if (shiftKey && selection) deps.setSelection({ start: selection.start, end: { row: Math.max(0, selection.end.row - 1), col: selection.end.col } })
            else deps.navigateTo(activeCell.row - 1, activeCell.col)
          }
          break
        case 'ArrowDown':
          e.preventDefault()
          if (activeCell) {
            if (shiftKey && selection) deps.setSelection({ start: selection.start, end: { row: Math.min(rowCount - 1, selection.end.row + 1), col: selection.end.col } })
            else deps.navigateTo(activeCell.row + 1, activeCell.col)
          }
          break
        case 'ArrowLeft':
          e.preventDefault()
          if (activeCell) {
            if (shiftKey && selection) deps.setSelection({ start: selection.start, end: { row: selection.end.row, col: Math.max(0, selection.end.col - 1) } })
            else deps.navigateTo(activeCell.row, deps.findNearestVisibleCol(activeCell.col - 1, -1))
          }
          break
        case 'ArrowRight':
          e.preventDefault()
          if (activeCell) {
            if (shiftKey && selection) deps.setSelection({ start: selection.start, end: { row: selection.end.row, col: Math.min(colCount - 1, selection.end.col + 1) } })
            else deps.navigateTo(activeCell.row, deps.findNearestVisibleCol(activeCell.col + 1, 1))
          }
          break
        case 'Tab':
          e.preventDefault()
          if (activeCell) {
            shiftKey
              ? deps.navigateTo(activeCell.row, deps.findNearestVisibleCol(activeCell.col - 1, -1))
              : deps.navigateTo(activeCell.row, deps.findNearestVisibleCol(activeCell.col + 1, 1))
          }
          break
        case 'Enter':
          e.preventDefault()
          if (activeCell) {
            if (shiftKey) { deps.navigateTo(activeCell.row - 1, activeCell.col) }
            else {
              const col = columns[activeCell.col]
              col?.editable ? deps.startEditing() : deps.navigateTo(activeCell.row + 1, activeCell.col)
            }
          }
          break
        case 'F2':
          e.preventDefault(); deps.startEditing(); break
        case 'Delete':
        case 'Backspace':
          e.preventDefault()
          if (activeCell) deps.onCellChange?.(activeCell.row, activeCell.col, '')
          break
        default:
          if (key.length === 1 && !isCmd && activeCell) {
            const col = columns[activeCell.col]
            if (col?.editable) { deps.setIsEditing(true); deps.setEditValue(key); e.preventDefault() }
          }
          break
      }
    } else {
      // While editing
      switch (key) {
        case 'Enter':
          e.preventDefault(); deps.commitEdit()
          if (activeCell) deps.navigateTo(activeCell.row + 1, activeCell.col)
          break
        case 'Tab':
          e.preventDefault(); deps.commitEdit()
          if (activeCell) {
            shiftKey
              ? deps.navigateTo(activeCell.row, deps.findNearestVisibleCol(activeCell.col - 1, -1))
              : deps.navigateTo(activeCell.row, deps.findNearestVisibleCol(activeCell.col + 1, 1))
          }
          break
        case 'Escape':
          e.preventDefault(); deps.cancelEdit(); break
        case 'ArrowUp':
        case 'ArrowDown':
          e.preventDefault(); deps.commitEdit()
          if (activeCell) deps.navigateTo(activeCell.row + (key === 'ArrowDown' ? 1 : -1), activeCell.col)
          break
      }
    }
  }
}
